import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Share, X } from 'lucide-react';
import { Button } from './ui/button';
import { pwa } from '../lib/pwa';
import IosPwaTutorialModal from './pwa/IosPwaTutorialModal';

/**
 * PwaInstallBanner — Phase 46 · Feb 2026 (Phase 47 iOS tutorial hook).
 * ------------------------------------------------------------------
 * A single low-key banner mounted globally in <AppShell>. Two modes:
 *
 *   - Android / desktop Chrome: listens for `beforeinstallprompt`,
 *     stashes the event and shows a discreet "Installa Nymia" card
 *     with a real Install button that triggers `event.prompt()`.
 *   - iOS Safari: no automatic prompt exists — shows a small tip
 *     with the "Share → Add to Home Screen" hint AND a
 *     "Come si installa?" link that opens a 3-step visual tutorial.
 *
 * Conditions to appear:
 *   - Not already running as standalone.
 *   - Not snoozed by the user in the last 7 days.
 *   - Not on a critical route (checkout, live, o2o session, wizard).
 *
 * Phase 47 · iOS tutorial:
 *   - Rendered on demand via <IosPwaTutorialModal/>.
 *   - Two entry points:
 *       a) "Come si installa?" link inside the banner (explicit).
 *       b) Auto-shown when the user X-dismisses the iOS banner
 *          without installing (respects a separate 7-day cooldown
 *          so we don't nag on every dismissal).
 */
const PwaInstallBanner = () => {
  const { t } = useTranslation('pwa');
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosFallback, setIosFallback] = useState(false);
  // Phase 47 — controls the iOS tutorial modal.
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const canShow = useCallback(() => {
    if (pwa.isStandalone()) return false;
    if (pwa.isSnoozed()) return false;
    if (pwa.isOnCriticalRoute()) return false;
    return true;
  }, []);

  // Phase 46 · polish — auto-hide the banner if the user navigates to a
  // critical route (checkout, live, onboarding wizard). This runs on
  // every location change so the banner never covers a live session
  // that was joined AFTER the banner had already been shown.
  useEffect(() => {
    if (pwa.isOnCriticalRoute()) {
      setVisible(false);
      setTutorialOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!canShow()) return;

    // Android / desktop Chrome path.
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // Clear banner if the browser installs the app in the background.
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS fallback (any browser) — surface a small hint after 6s.
    // We wait a bit so the user has some context before nudging.
    let iosTimer = null;
    if (pwa.isIos()) {
      iosTimer = window.setTimeout(() => {
        setIosFallback(true);
        setVisible(true);
      }, 6000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [canShow]);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // Whatever the user picks we clear the banner. If they dismissed,
      // beforeinstallprompt won't fire again in this session.
      setDeferredPrompt(null);
      setVisible(false);
      if (outcome === 'dismissed') {
        pwa.snooze();
      }
    } catch (_e) {
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const onDismiss = () => {
    // If it's the iOS banner and the user hasn't seen the tutorial in
    // the last 7 days, auto-open it so they know HOW to install. Even
    // if they X-dismiss the tutorial too, both snoozes prevent re-nag.
    const shouldAutoOpenTutorial = iosFallback && !pwa.isIosTutorialSnoozed();
    pwa.snooze();
    setVisible(false);
    setDeferredPrompt(null);
    setIosFallback(false);
    if (shouldAutoOpenTutorial) {
      setTutorialOpen(true);
    }
  };

  const openTutorial = () => {
    setTutorialOpen(true);
  };

  const closeTutorial = (snooze = true) => {
    setTutorialOpen(false);
    if (snooze) pwa.snoozeIosTutorial();
  };

  return (
    <>
      {visible && (
        <div
          className="fixed inset-x-3 bottom-3 z-40 mx-auto max-w-md rounded-2xl border border-amber-400/40 bg-black/90 p-3 shadow-lg backdrop-blur md:bottom-4"
          role="dialog"
          aria-labelledby="pwa-install-title"
          data-testid={iosFallback ? 'pwa-ios-tip' : 'pwa-install-banner'}
        >
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/20 gold-text">
              {iosFallback ? <Share className="h-4 w-4" /> : <Download className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <p id="pwa-install-title" className="text-sm font-semibold text-foreground">
                {iosFallback ? t('ios.title') : t('install.title')}
              </p>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                {iosFallback
                  ? (pwa.isIosSafari() ? t('ios.description') : t('ios.not_safari_description'))
                  : t('install.description')}
              </p>
              {iosFallback ? (
                <button
                  type="button"
                  onClick={openTutorial}
                  className="mt-2 inline-flex items-center text-[12px] font-semibold gold-text underline-offset-2 hover:underline focus:outline-none focus:underline"
                  data-testid="pwa-ios-how-link"
                >
                  {t('ios.how_link')}
                </button>
              ) : (
                <div className="mt-3 flex gap-2">
                  <Button
                    onClick={onInstall}
                    className="h-8 gold-gradient px-3 text-xs font-semibold text-black"
                    data-testid="pwa-install-cta"
                  >
                    {t('install.cta')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={onDismiss}
                    className="h-8 px-3 text-xs text-muted-foreground"
                    data-testid="pwa-install-dismiss"
                  >
                    {t('install.later')}
                  </Button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-white/[0.05]"
              aria-label={t('install.later')}
              data-testid={iosFallback ? 'pwa-ios-close' : 'pwa-install-close'}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Phase 47 · iOS Add-to-Home-Screen tutorial */}
      <IosPwaTutorialModal
        open={tutorialOpen}
        onOpenChange={(o) => (o ? setTutorialOpen(true) : closeTutorial(true))}
        onLater={() => closeTutorial(true)}
        onGotIt={() => closeTutorial(true)}
      />
    </>
  );
};

export default PwaInstallBanner;
